import { STOCK_OPNAME_STATUS_LABELS } from "@artisancode/api-types";
import { ArrowLeft, CheckCircle2, Save } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useAddStockOpnameItems,
  useCompleteStockOpname,
  useRemoveStockOpnameItem,
  useStockOpname,
  useUpdateStockOpname,
} from "@/hooks/use-stock-opnames";
import { useStockReasons } from "@/hooks/use-stock-reasons";
import { formatDateWIB } from "@/lib/date-utils";

import { StockOpnameAddItem } from "./stock-opname-add-item";
import { StockOpnameCompleteDialog } from "./stock-opname-complete-dialog";
import { StockOpnameItemsTable } from "./stock-opname-items-table";
import { StockOpnameMetricsBar } from "./stock-opname-metrics-bar";
import { useStockOpnameForm } from "./use-stock-opname-form";

import type { StockOpname, StockOpnameType } from "@artisancode/api-types";

export function stockOpnameListPath(type: StockOpnameType) {
  return type === "raw_material"
    ? "/stock-opname/raw-materials"
    : "/stock-opname/products";
}

/** Full page for counting (draft) or reviewing (completed/cancelled) one opname session. */
export function StockOpnameSessionPage() {
  const { opnameId } = useParams();
  const { data: opname, isLoading } = useStockOpname(opnameId);

  if (isLoading) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        Memuat sesi opname...
      </p>
    );
  }
  if (!opname) {
    return <EmptyState title="Sesi opname tidak ditemukan" description="" />;
  }

  // Keyed so the local count state resets when switching sessions.
  return <StockOpnameSession key={opname.id} opname={opname} />;
}

function StockOpnameSession({ opname }: { opname: StockOpname }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const backPath = stockOpnameListPath(opname.type);
  const { data: reasons = [] } = useStockReasons({ is_active: true });
  const updateMutation = useUpdateStockOpname();
  const completeMutation = useCompleteStockOpname();
  const addItems = useAddStockOpnameItems();
  const removeItem = useRemoveStockOpnameItem();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const form = useStockOpnameForm({
    opname,
    reasons,
    onSaveDraft: async (data) => {
      await updateMutation.mutateAsync({ id: opname.id, ...data });
    },
    onComplete: async (data) => {
      await completeMutation.mutateAsync({ id: opname.id, ...data });
    },
    onClose: () => navigate(backPath),
  });

  const statusMeta = STOCK_OPNAME_STATUS_LABELS[opname.status];
  const existingItemIds = new Set((opname.items ?? []).map((i) => i.itemId));
  const actions = !form.isLocked && (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={form.handleSaveDraft}
        disabled={form.isSubmitting}
        className="min-h-[40px] gap-1.5 text-xs md:h-9 md:min-h-0"
      >
        <Save className="h-3.5 w-3.5" />
        Simpan Draft
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={() => form.validateCompletion() && setConfirmOpen(true)}
        disabled={form.isSubmitting}
        className="min-h-[40px] gap-1.5 bg-emerald-600 text-xs text-white hover:bg-emerald-700 md:h-9 md:min-h-0"
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        Selesaikan Opname
      </Button>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="mt-0.5 h-10 w-10 shrink-0"
        >
          <Link to={backPath} aria-label="Kembali ke daftar opname">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-lg font-bold">{opname.code}</h1>
            <Badge variant={statusMeta.variant} className="capitalize">
              {statusMeta.label}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {opname.type === "raw_material"
                ? "Bahan Baku"
                : "Produk Siap Jual"}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Tanggal:{" "}
            <span className="font-medium text-foreground">
              {formatDateWIB(opname.date, { dateStyle: "long" })}
            </span>
            {opname.createdByName && ` • Dibuat oleh ${opname.createdByName}`}
          </p>
        </div>
        {!isMobile && actions && (
          <div className="ml-auto flex shrink-0 gap-2">{actions}</div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="shrink-0 text-xs text-muted-foreground">
          Catatan Sesi:
        </span>
        {form.isLocked ? (
          <span className="truncate text-xs italic text-foreground">
            {opname.note || "Tidak ada catatan"}
          </span>
        ) : (
          <Input
            aria-label="Catatan sesi"
            value={form.notes}
            onChange={(e) => form.setNotes(e.target.value)}
            placeholder="Catatan pelaksanaan stock opname..."
            className="h-10 bg-background text-xs md:h-9 md:max-w-md"
          />
        )}
      </div>

      {!form.isLocked && (
        <StockOpnameAddItem
          type={opname.type}
          existingItemIds={existingItemIds}
          disabled={addItems.isPending}
          onAdd={(itemId) =>
            addItems.mutate({ id: opname.id, itemIds: [itemId] })
          }
        />
      )}

      <Card className="gap-0 overflow-hidden py-0">
        <StockOpnameMetricsBar
          totalItems={form.totalItems}
          matchCount={form.matchCount}
          minusCount={form.minusCount}
          plusCount={form.plusCount}
          netDiffValue={form.netDiffValue}
        />
        <StockOpnameItemsTable
          opnameType={opname.type}
          items={form.filteredItems}
          reasons={reasons}
          query={form.query}
          onQueryChange={form.setQuery}
          onlyDiscrepancies={form.onlyDiscrepancies}
          onToggleOnlyDiscrepancies={() =>
            form.setOnlyDiscrepancies(!form.onlyDiscrepancies)
          }
          discrepancyCount={form.discrepancyCount}
          onOnHandChange={form.handleOnHandChange}
          onRepackingChange={form.handleRepackingChange}
          onReasonChange={form.handleReasonChange}
          onNoteChange={form.handleNoteChange}
          isLocked={form.isLocked}
          errors={form.errors}
          onRemove={(lineId) => removeItem.mutate({ id: opname.id, lineId })}
          emptyText={
            form.totalItems === 0
              ? "Belum ada barang. Cari barang di atas untuk mulai menghitung."
              : "Tidak ada barang yang cocok dengan pencarian."
          }
        />
      </Card>

      {/* Mobile: sticky above the bottom nav (the layout's bottom padding keeps it clear). */}
      {isMobile && actions && (
        <div className="sticky bottom-0 z-20 grid grid-cols-2 gap-2 rounded-xl border bg-background/95 p-2 shadow-lg backdrop-blur">
          {actions}
        </div>
      )}

      <StockOpnameCompleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        code={opname.code}
        totalItems={form.totalItems}
        discrepancyCount={form.discrepancyCount}
        netDiffValue={form.netDiffValue}
        isSubmitting={form.isSubmitting}
        onConfirm={form.handleComplete}
      />
    </div>
  );
}
